import axios, { AxiosError, InternalAxiosRequestConfig } from 'axios';
import { getAccessToken, getRefreshToken, getUserId } from '@/core/utils/tokenUtils';
import { redirectToAuth } from '@/core/utils/redirectToAuth';
import { handleForbiddenApiError } from './handleForbiddenApiError';
import { getBadRequestMessage } from './badRequestError';
import { useApiErrorDialog } from '@/shared/model/apiErrorDialog';
import { hasInlineErrorDisplay } from './requestErrorMode';
import {
    expireSession,
    refreshSession,
    SessionChangedError,
    SessionExpiredError,
} from './sessionRefresh';

/** Выполняет HTTP-запросы админки с общей обработкой ответов API */
export const apiHttpClient = axios;

type RetryableRequestConfig = InternalAxiosRequestConfig & {
    _retry?: boolean;
    _sessionUserId?: string | null;
};

let isSessionExpirationHandled = false;

/** Открывает вход после завершения сессии сотрудника */
const handleSessionExpiration = () => {
    if (isSessionExpirationHandled || getAccessToken() || getRefreshToken()) return;

    isSessionExpirationHandled = true;
    redirectToAuth();

    queueMicrotask(() => {
        isSessionExpirationHandled = false;
    });
};

apiHttpClient.interceptors.request.use((config: RetryableRequestConfig) => {
    const userId = getUserId();
    const sessionChanged = config._sessionUserId !== undefined && config._sessionUserId !== userId;

    if (sessionChanged) throw new SessionChangedError();

    config._sessionUserId = userId;
    const token = getAccessToken();

    if (token) config.headers.Authorization = `Bearer ${token}`;
    else config.headers.delete('Authorization');

    return config;
});

apiHttpClient.interceptors.response.use(
    (response) => response,
    async (error: AxiosError) => {
        const originalRequest = error.config as RetryableRequestConfig | undefined;
        const isAuthRequest = /\/auth\//.test(originalRequest?.url ?? '');
        const isRefreshRequest = /\/auth\/refresh(?:\?|$)/.test(originalRequest?.url ?? '');

        if (error.response?.status === 400 && !isRefreshRequest && !hasInlineErrorDisplay(error)) {
            useApiErrorDialog.getState().show(getBadRequestMessage(error));

            return Promise.reject(error);
        }

        if (error.response?.status === 403 && !isRefreshRequest && !hasInlineErrorDisplay(error)) {
            handleForbiddenApiError(error);

            return Promise.reject(error);
        }

        if (error.response?.status !== 401 || isAuthRequest || !originalRequest) {
            return Promise.reject(error);
        }

        const sessionChanged = originalRequest._sessionUserId !== undefined
            && originalRequest._sessionUserId !== getUserId();

        if (sessionChanged) return Promise.reject(new SessionChangedError());

        try {
            const currentToken = getAccessToken();
            const requestToken = originalRequest.headers.Authorization;
            const hasNewToken = currentToken && requestToken !== `Bearer ${currentToken}`;

            if (originalRequest._retry) {
                if (hasNewToken) throw new SessionChangedError();

                expireSession(getRefreshToken());
            }

            const token = hasNewToken
                ? currentToken
                : (await refreshSession()).accessToken;

            originalRequest._retry = true;
            originalRequest.headers.Authorization = `Bearer ${token}`;

            return apiHttpClient(originalRequest);
        } catch (refreshError: unknown) {
            if (refreshError instanceof SessionExpiredError) handleSessionExpiration();

            return Promise.reject(refreshError);
        }
    },
);
